'use client'

import React, { createContext, useContext, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useNavBadges } from '@/hooks/useShellData'
import { usePersona } from '@/lib/persona'
import { SidebarPersona } from '@/components/layout/SidebarPersona'
import { NavItemLabel } from '@/components/layout/NavItemLabel'
import { Logomark } from '@/components/brand'
import {
  AUTO_NAME,
  AUTO_SUBLABEL,
  badgeSpoken,
  badgeText,
  isNavItemActive,
  isNeedsBadge,
  navItemText,
  visibleNavSections,
  type NavItem,
  type NavSection,
} from '@/lib/navigation'

// Sidebar context for collapse state
interface SidebarContextType {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  toggle: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const toggle = () => setIsCollapsed((value) => !value)

  return <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, toggle }}>{children}</SidebarContext.Provider>
}

/** The sections this window's persona sees (plans/00 5.3). Shared with the mobile sheet and the palette. */
export function useVisibleNavSections(): NavSection[] {
  const { persona } = usePersona()
  return visibleNavSections(persona)
}

// Navigation link. Text tones on the navy rail: idle white/70 is 8.7:1 on the
// rail and section titles white/55 are 5.9:1, both above WCAG AA (Rule 14).
interface NavLinkProps {
  item: NavItem
  isCollapsed?: boolean
  badge?: number
}

function NavLink({ item, isCollapsed, badge }: NavLinkProps) {
  const { href, icon: Icon, badge: badgeKey } = item
  const pathname = usePathname()
  const isActive = isNavItemActive(pathname, href)
  const count = badgeText(badge)
  const needs = isNeedsBadge(badgeKey)

  // The accessible name comes from the content, not aria-label, so 司令室 keeps lang="ja" (WCAG 3.1.2).
  const linkContent = (
    <Link
      href={href}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'group relative flex items-center gap-3 rounded-xl px-3 py-2',
        'text-sm font-medium',
        'transition-all duration-200 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
        isActive
          ? 'bg-primary-foreground/[0.12] text-primary-foreground shadow-soft'
          : 'text-primary-foreground/70 hover:translate-x-1 hover:bg-primary-foreground/10 hover:text-primary-foreground',
        isCollapsed && 'justify-center px-2 hover:translate-x-0'
      )}
    >
      {/* Active marker: a cornflower bar, so the selected item reads without colour alone. */}
      {isActive && (
        <span
          aria-hidden='true'
          className='absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-brand-cornflower'
        />
      )}
      <Icon
        strokeWidth={1.5}
        aria-hidden='true'
        className={cn(
          'h-5 w-5 shrink-0 transition-colors duration-200',
          isActive ? 'text-primary-foreground' : 'text-primary-foreground/60 group-hover:text-primary-foreground'
        )}
      />

      {isCollapsed ? (
        <span className='sr-only'>
          <NavItemLabel item={item} />
        </span>
      ) : (
        <span className='truncate' title={navItemText(item)}>
          <NavItemLabel item={item} />
        </span>
      )}

      {count && (
        <span
          aria-hidden='true'
          className={cn(
            'ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5',
            'text-xs font-semibold text-primary-foreground',
            needs ? 'bg-needs-you' : isActive ? 'bg-primary-foreground/20' : 'bg-brand-cornflower/30',
            isCollapsed && 'absolute -right-1 -top-1 h-5 min-w-5 px-1'
          )}
        >
          {count}
        </span>
      )}
      {count && <span className='sr-only'>, {badgeSpoken(badgeKey, count)}</span>}
    </Link>
  )

  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side='right' className='ml-2'>
          <NavItemLabel item={item} />
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}

export function Sidebar() {
  const { isCollapsed, setIsCollapsed } = useSidebar()
  const sections = useVisibleNavSections()
  const badges = useNavBadges()

  return (
    <div
      className={cn(
        'hidden flex-col md:flex',
        'fixed bottom-0 left-0 top-0 z-fixed',
        'bg-primary text-primary-foreground',
        'border-r border-primary-foreground/[0.08]',
        'transition-all duration-300 ease-out',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Brand block: the Supervity logomark tile, product name on two lines, sub-label (plans/09 5.2, P9.10). */}
      <div
        className={cn(
          'flex h-20 items-center px-4',
          'border-b border-primary-foreground/[0.08]',
          isCollapsed && 'justify-center px-2'
        )}
      >
        <Link
          href='/'
          className='group flex min-w-0 items-center gap-2.5 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
        >
          {/* The SVG carries its own white tile and radius, so no frame around it. Decorative: the name names the link. */}
          <span className='shrink-0 rounded-xl transition-all duration-300 ease-out group-hover:shadow-accent'>
            <Logomark variant='light' size={isCollapsed ? 36 : 40} alt='' />
          </span>

          {isCollapsed ? (
            <span className='sr-only'>
              {AUTO_NAME}, {AUTO_SUBLABEL}
            </span>
          ) : (
            <span className='flex min-w-0 flex-col gap-0.5 transition-transform duration-200 group-hover:translate-x-0.5'>
              <span
                title={AUTO_NAME}
                className='line-clamp-2 font-display text-base font-semibold leading-tight tracking-tight text-primary-foreground'
              >
                {AUTO_NAME}
              </span>
              <span className='truncate text-xs text-primary-foreground/60'>{AUTO_SUBLABEL}</span>
            </span>
          )}
        </Link>
      </div>

      <nav aria-label='Primary navigation' className='scrollbar-hide flex-1 overflow-y-auto px-3 py-4'>
        {sections.map((section, sectionIndex) => (
          <div key={section.title} className={cn(sectionIndex > 0 && 'mt-5')}>
            {!isCollapsed && (
              <p className='mb-2 px-3 text-xs font-semibold uppercase tracking-widest text-primary-foreground/55'>
                {section.title}
              </p>
            )}
            <ul className='space-y-1' aria-label={isCollapsed ? section.title : undefined}>
              {section.items.map((item) => (
                <li key={item.id}>
                  <NavLink item={item} isCollapsed={isCollapsed} badge={item.badge ? badges[item.badge] : undefined} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className='border-t border-primary-foreground/[0.08] p-3'>
        <SidebarPersona isCollapsed={isCollapsed} />
      </div>

      <div className='border-t border-primary-foreground/[0.08] p-3'>
        <Button
          variant='ghost'
          size={isCollapsed ? 'icon-sm' : 'sm'}
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn(
            'w-full text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground',
            'focus-visible:ring-brand-cornflower',
            isCollapsed && 'px-0'
          )}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <Icons.panelOpen aria-hidden='true' className='h-4 w-4' strokeWidth={1.5} />
          ) : (
            <>
              <Icons.panelClose aria-hidden='true' className='h-4 w-4' strokeWidth={1.5} />
              <span className='ml-2'>Collapse</span>
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
